import {
  DUAL_ENROLLMENT_BODY_INDEX,
  DUAL_ENROLLMENT_TYPE,
  EnrollmentContext,
  EnrollmentContextResponseDto,
  ENROLLMENT_COMPLETE_STATUS,
  ENROLLMENT_CONTEXT_SUCCESS,
  LIFE_EVENT_BODY_INDEX,
  LIFE_EVENT_ENROLLMENT_TYPE,
  LIFE_EVENT_HEADER_INDEX,
  NEW_HIRE_BODY_INDEX,
  NEW_HIRE_ENROLLMENT_TYPE,
  OPEN_ENROLLMENT_BODY_INDEX,
  OPEN_ENROLLMENT_HEADER_INDEX,
  OPEN_ENROLLMENT_TYPE,
  WELCOME_HEADER_INDEX,
  CONTINUE_BODY_INDEX,
  CONTINUE_HEADER_INDEX,
  ENROLLMENT_STARTED_STATUS,
} from 'theSource/queries';

const CONTINUE_ENROLLMENT_TYPE = 'continue';

const invalidContext = (
  enrollmentContexts: EnrollmentContextResponseDto | undefined
) => {
  if (enrollmentContexts) {
    return enrollmentContexts.status !== ENROLLMENT_CONTEXT_SUCCESS;
  }
  return true; // enrollmentContexts is undefined, return true
};

export const isContinueEnrollment = (
  enrollmentContexts: EnrollmentContextResponseDto | undefined
) => {
  const contexts = enrollmentContexts?.data as EnrollmentContext[];
  return contexts.some((context) => {
    return context.status === ENROLLMENT_STARTED_STATUS;
  });
};

export const isLifeEventContext = (
  enrollmentContexts: EnrollmentContextResponseDto | undefined
) => {
  if (invalidContext(enrollmentContexts)) {
    return false;
  }
  const contexts = enrollmentContexts?.data as EnrollmentContext[];

  // if open enrollment context is life event, return true
  return contexts.some((context) => {
    return (
      context.enrollment_type_lookup_code === LIFE_EVENT_ENROLLMENT_TYPE &&
      !ENROLLMENT_COMPLETE_STATUS.includes(context.status)
    );
  });
};

export const isLifeEventNotStarted = (
  enrollmentContexts: EnrollmentContextResponseDto | undefined
) => {
  if (invalidContext(enrollmentContexts)) {
    return false;
  }
  const contexts = enrollmentContexts?.data as EnrollmentContext[];

  // if open enrollment context is life event and not started, return true
  return contexts.some((context) => {
    return (
      context.enrollment_type_lookup_code === LIFE_EVENT_ENROLLMENT_TYPE &&
      (context.status === 'not_started' || context.status === 'not_open') &&
      context.subscriber_life_event === undefined
    );
  });
};

export const lifeEventID = (
  enrollmentContexts: EnrollmentContextResponseDto | undefined
) => {
  if (invalidContext(enrollmentContexts)) {
    return undefined;
  }
  let id: string | undefined;

  // if open enrollment context is life event and not started, return true
  enrollmentContexts?.data.forEach((context) => {
    if (
      context.enrollment_type_lookup_code === LIFE_EVENT_ENROLLMENT_TYPE &&
      ['not_started', 'started'].includes(context.status)
    ) {
      id = context.subscriber_life_event?.id;
    }
  });
  return id;
};

export const filterEnrollmentContexts = (
  enrollmentContexts: EnrollmentContextResponseDto | undefined
) => {
  if (invalidContext(enrollmentContexts)) {
    return false;
  }
  const contexts = enrollmentContexts?.data as EnrollmentContext[];

  // if any enrollment context is open, return true
  return contexts.some((context) => {
    if (
      context.enrollment_type_lookup_code === LIFE_EVENT_ENROLLMENT_TYPE &&
      context.status === 'complete'
    ) {
      return false;
    } else if (!ENROLLMENT_COMPLETE_STATUS.includes(context.status)) {
      return true;
    }
    return false;
  });
};

export const selectHeaderText = (
  enrollmentContexts: EnrollmentContextResponseDto | undefined,
  headers: string[],
  orgName: string
) => {
  if (invalidContext(enrollmentContexts)) {
    return null;
  }

  let context = getCurrentContext(enrollmentContexts);

  if (context !== '' && isContinueEnrollment(enrollmentContexts)) {
    context = 'continue';
  }

  switch (context) {
    case CONTINUE_ENROLLMENT_TYPE:
      return headers[CONTINUE_HEADER_INDEX];
    case NEW_HIRE_ENROLLMENT_TYPE:
      return `${headers[WELCOME_HEADER_INDEX]} ${orgName}!`;
    case OPEN_ENROLLMENT_TYPE:
      return headers[OPEN_ENROLLMENT_HEADER_INDEX];
    case LIFE_EVENT_ENROLLMENT_TYPE:
      return `${headers[LIFE_EVENT_HEADER_INDEX]}`;
    case DUAL_ENROLLMENT_TYPE:
      return `${headers[WELCOME_HEADER_INDEX]} ${headers[OPEN_ENROLLMENT_HEADER_INDEX]}`;
  }
  return null;
};

export const selectBodyText = (
  enrollmentContexts: EnrollmentContextResponseDto | undefined,
  bodyTexts: string[]
) => {
  if (invalidContext(enrollmentContexts)) {
    return null;
  }

  let context = getCurrentContext(enrollmentContexts);

  if (context !== '' && isContinueEnrollment(enrollmentContexts)) {
    context = 'continue';
  }

  switch (context) {
    case CONTINUE_ENROLLMENT_TYPE:
      return bodyTexts[CONTINUE_BODY_INDEX];
    case NEW_HIRE_ENROLLMENT_TYPE:
      return bodyTexts[NEW_HIRE_BODY_INDEX];
    case OPEN_ENROLLMENT_TYPE:
      return bodyTexts[OPEN_ENROLLMENT_BODY_INDEX];
    case LIFE_EVENT_ENROLLMENT_TYPE:
      return bodyTexts[LIFE_EVENT_BODY_INDEX];
    case DUAL_ENROLLMENT_TYPE:
      return bodyTexts[DUAL_ENROLLMENT_BODY_INDEX];
  }
  return null;
};

export const getCurrentContext = (
  enrollmentContexts: EnrollmentContextResponseDto | undefined
) => {
  if (invalidContext(enrollmentContexts)) {
    return '';
  }

  // if (dualEnrollment(enrollmentContexts)) {
  //   return 'dual';
  // }

  const activeContextIndex = getActiveContextIndex(enrollmentContexts);

  if (activeContextIndex < 0) {
    return '';
  }

  const contexts = enrollmentContexts?.data as EnrollmentContext[];
  return contexts[activeContextIndex].enrollment_type_lookup_code;
};

export const getActiveContextIndex = (
  enrollmentContexts: EnrollmentContextResponseDto | undefined
) => {
  const contexts = enrollmentContexts?.data as EnrollmentContext[];
  const activeContextIndex = contexts?.findIndex((context) => {
    return !ENROLLMENT_COMPLETE_STATUS?.includes(context.status);
  });
  return activeContextIndex;
};
